import { computed } from 'vue';
import { DATA_TYPES } from '@/models/dashboards/metrics.constants';
import { DISPLAY_STRATEGY } from '@/app/dashboards/reportConstants';

/**
 * Converting formats to types dashing-ui handles
 */
const FORMAT_TYPES = {
  NORMAL: 'normal', // No special formatting - displays raw number values
  PERCENT: 'percent',
  CURRENCY: 'currency',
  NUMBER: 'number',
  DURATION: 'duration',
  ABBREVIATED: 'abbr',
};

const TIME_UNIT_OPTIONS = {
  MILLISECONDS: 'millisecond',
  SECONDS: 'second',
};

/**
 * Maps data types and custom metric format types to standard format types
 * Handles both backend data types and custom metric naming conventions
 */
export const formatTypeMap = {
  PERCENTAGE: FORMAT_TYPES.PERCENT,
  MONETARY: FORMAT_TYPES.CURRENCY,
  INTEGER: FORMAT_TYPES.NUMBER,
  FLOAT: FORMAT_TYPES.NUMBER,
  DURATION: FORMAT_TYPES.DURATION,
  SECONDS: FORMAT_TYPES.DURATION,
  MILLISECONDS: FORMAT_TYPES.DURATION,
  default: FORMAT_TYPES.NUMBER,
};
export function useMetricFormatting({
  dataType,
  isCustomMetricReportTile,
  customMetricDetails,
  currency,
  displayStrategy,
} = {}) {
  const computedDataType = computed(() => {
    if (displayStrategy?.value === DISPLAY_STRATEGY.PERCENT_OF_GRAND_TOTAL) return 'PERCENTAGE';
    return dataType?.value;
  });

  /**
   * Gets the appropriate format type for the current metric
   * Handles both regular metrics and custom metrics
   */
  const formatType = computed(() => {
    if (isCustomMetricReportTile?.value) {
      const customFormat = customMetricDetails?.value?.originalMetricFormat;
      return formatTypeMap[customFormat] || formatTypeMap.default;
    }

    const currentDataType = computedDataType.value;
    return formatTypeMap[currentDataType] || formatTypeMap.default;
  });

  const isUnknownFormat = computed(() => {
    let currentDataType = computedDataType.value;
    if (isCustomMetricReportTile?.value) {
      currentDataType = customMetricDetails?.value?.originalMetricFormat;
    }
    return formatTypeMap[currentDataType] === undefined;
  });

  /**
   * Utility function to check if current metric is float type
   */
  const isFloatMetric = computed(
    () =>
      computedDataType?.value === DATA_TYPES.FLOAT ||
      customMetricDetails?.value?.originalMetricFormat === 'FLOAT',
  );

  /**
   * Utility function to check if current metric is percentage type
   */
  const isPercentageMetric = computed(
    () =>
      formatType.value === FORMAT_TYPES.PERCENT ||
      customMetricDetails?.value?.originalMetricFormat === 'PERCENTAGE',
  );
  const isDurationMetric = computed(() => formatType.value === FORMAT_TYPES.DURATION);

  /**
   * Utility function to check if current metric is monetary type
   */
  const isMonetaryMetric = computed(() => formatType.value === FORMAT_TYPES.CURRENCY);

  function getFormatConfig({
    value = 0,
    isHover = false,
    isSmallTile = false,
    disableAbbreviation = false,
    isDoughnutChart = false,
  }) {
    let formatOptions = {
      minimumFractionDigits: isPercentageMetric.value || isFloatMetric.value ? 2 : 0,
      maximumFractionDigits: isPercentageMetric.value || isFloatMetric.value ? 2 : 0,
      useGrouping: true,
    };
    let durationOptions = {};
    let type = formatType.value;

    if (isDurationMetric.value) {
      durationOptions = {
        rawUnit: TIME_UNIT_OPTIONS[computedDataType.value] || TIME_UNIT_OPTIONS.SECONDS,
        smallestUnit: TIME_UNIT_OPTIONS[computedDataType.value] || TIME_UNIT_OPTIONS.SECONDS,
      };
    }

    if (isFloatMetric.value && !isHover && !disableAbbreviation) {
      if (value < 1) {
        formatOptions = {
          ...formatOptions,
          minimumFractionDigits: 1,
          maximumFractionDigits: 1,
        };
      } else {
        formatOptions = {
          ...formatOptions,
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        };
      }
    }

    if (isMonetaryMetric.value) {
      formatOptions = { ...formatOptions, currency: currency.value };
      if (value < 1000 || isHover) {
        formatOptions = {
          ...formatOptions,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        };
      } else {
        formatOptions = {
          ...formatOptions,
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        };
      }
    }

    const isAbbreviated =
      !disableAbbreviation && !isHover && type === FORMAT_TYPES.NUMBER && !isUnknownFormat.value;
    if (isAbbreviated) {
      type = FORMAT_TYPES.ABBREVIATED;
      if (value >= 1_000_000) {
        formatOptions = {
          ...formatOptions,
          notation: 'compact',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        };
      }
      // Allow truncation on smaller values if a single metric report
      if (isSmallTile && value >= 100_000 && value < 1_000_000) {
        formatOptions = {
          ...formatOptions,
          notation: 'compact',
          minimumFractionDigits: 1,
          maximumFractionDigits: 1,
        };
      }
    }

    if (isDoughnutChart) {
      type = FORMAT_TYPES.PERCENT;
      formatOptions = {
        ...formatOptions,
        minimumFractionDigits: 1,
        maximumFractionDigits: 2,
      };
    }
    return { format: type, formatOptions, durationOptions };
  }

  /**
   * Creates consistent format configuration for comparison data
   * Used in both single metric and table metric reports
   */
  function getComparisonFormatConfig({
    reportingValue,
    comparisonValue,
    isHover = false,
    disableAbbreviation = false,
  }) {
    const reportingOptions = getFormatConfig({
      value: reportingValue,
      isHover,
      disableAbbreviation,
    });
    const comparisonOptions = getFormatConfig({
      value: comparisonValue,
      isHover,
      disableAbbreviation,
    });

    return {
      reportingFormatType: reportingOptions.format,
      reportingFormatOptions: {
        formatOptions: reportingOptions.formatOptions,
        durationOptions: reportingOptions.durationOptions,
      },
      comparisonFormatType: comparisonOptions.format,
      comparisonFormatOptions: {
        formatOptions: comparisonOptions.formatOptions,
        durationOptions: comparisonOptions.durationOptions,
      },
    };
  }

  /**
   * Utility function to get format type as string (for backward compatibility)
   * Some components might expect string format types
   */
  const getFormatTypeString = () => formatType.value;

  return {
    getComparisonFormatConfig,
    getFormatTypeString,
    isPercentageMetric,
    isMonetaryMetric,
    isDurationMetric,
    formatTypeMap,
    getFormatConfig,
  };
}
